import { Checkbox, Fade, IconButton, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import React from 'react'

import NoDataImage from '../../assets/images/No-Data.png'
import { Ticket } from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import SupportTicketRow from './SupportTicketRow'
import SupportTicketRowSkeleton from './SupportTicketRow.skeleton'
import useStyles from './styles/SupportTicketsTable.styles'

interface SupportTicketsTableProps {
  tickets: Ticket[]
  loading: boolean
  /** True once a load has finished without error, so empty means empty. */
  settled: boolean
  /**
   * What an empty list MEANS. Only the parent knows whether a search is
   * narrowing it, and "nothing here" and "nothing matched" are different
   * facts — telling an operator the first when the second is true is a lie
   * the search itself created.
   */
  emptyMessage: string
  reloadData: () => void
  selectedIds: Set<string>
  onToggleSelected: (ticket: Ticket) => void
  onToggleAll: () => void
  allSelected: boolean
  someSelected: boolean
  /** True while a batch is in flight; freezes selection. */
  disabled?: boolean
}

/**
 * The desktop ticket list, in the portal's list shape (§2): a div-grid header
 * ending in a refresh IconButton, rows faded in, skeletons while loading, and
 * the shared No-Data asset when the list is genuinely empty.
 */
export default function SupportTicketsTable({
  tickets,
  loading,
  settled,
  emptyMessage,
  reloadData,
  selectedIds,
  onToggleSelected,
  onToggleAll,
  allSelected,
  someSelected,
  disabled
}: SupportTicketsTableProps) {
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <div className={classes.tableHead}>
        {/* Selects the LOADED rows, which is all this list can speak for — the
            queue is cursor-paginated, so "all" cannot mean the server's whole
            set without a claim the client is not able to make. `indeterminate`
            is what separates "some of these" from "none"; without it a partial
            selection announces as unchecked, i.e. as nothing selected. */}
        <div>
          <Checkbox
            size="small"
            color="primary"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={onToggleAll}
            disabled={disabled || !tickets.length}
            inputProps={{
              'aria-label': getTranslate('Select all loaded tickets')
            }}
          />
        </div>
        <div>
          <Typography variant="body1">{getTranslate('Priority')}</Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate('Number')}</Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate('Title')}</Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate('Affected')}</Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate('State')}</Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate('Assignment')}</Typography>
        </div>
        <div>
          <Typography variant="body1">{getTranslate('Updated')}</Typography>
        </div>
        <div className={classes.refreshButtonContainer}>
          <IconButton onClick={reloadData} aria-label={getTranslate('Refresh')}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {loading && !tickets.length
        ? Array(5)
            .fill('')
            .map((item, index) => (
              <SupportTicketRowSkeleton
                key={`support-ticket-skeleton${item}${index.toString()}`}
              />
            ))
        : tickets.map((ticket) => (
            <Fade in timeout={600} key={ticket.id}>
              <div data-cy="tableRowSupportTicketData">
                <SupportTicketRow
                  ticket={ticket}
                  selected={selectedIds.has(ticket.id)}
                  onToggleSelected={onToggleSelected}
                  selectionDisabled={disabled}
                />
              </div>
            </Fade>
          ))}

      {/* Only once a load has SETTLED. An empty list and a list that has not
          arrived are different states; showing "no data" for the second tells
          the operator something false. */}
      {settled && !tickets.length && (
        <div className={classes.noDataImageContainer}>
          <img src={NoDataImage} alt={emptyMessage} />
        </div>
      )}
    </div>
  )
}
